import { createRoot } from "react-dom/client";
import { fontFaceCss } from "@broadpaper/cloud-fonts";
import { App } from "./App.js";
import { interceptLinks } from "./router.js";
import { SharedDocument } from "./screens/SharedDocument.js";
import { Goodbye } from "./screens/Goodbye.js";
import "./styles/app.css";

/**
 * The typefaces, before anything is measured.
 *
 * Injected as a stylesheet rather than written into the CSS file, because the
 * rules come from the font package — so adding a face is one edit in one place and
 * the application cannot offer a face it has not loaded.
 *
 * `font-display: block`, which the package sets and which is against the usual
 * advice, matters most here: the designer measures text as soon as it can, and a
 * fallback face measured and then replaced makes the page breaks visibly move under
 * the user. A short invisible pause is better than a document that rearranges
 * itself while somebody is reading it.
 */
const fonts = document.createElement("style");
fonts.textContent = fontFaceCss("/fonts");
document.head.appendChild(fonts);

const root = document.getElementById("root");
if (!root) throw new Error("No #root to mount into.");
// Clears the pre-paint shell in index.html.
root.innerHTML = "";

/*
 * Two pages nobody signs in to, decided before anything else is loaded.
 *
 * A shared document is opened by a stranger, and the page after an account is
 * deleted is for somebody who no longer has a sign-in. Neither should load Clerk —
 * two third-party scripts, and a sign-in form flashing past — so neither goes
 * anywhere near `App`, whose first act is to ask who is signed in.
 */
const path = window.location.pathname;
const shared = /^\/s\/([0-9a-z]{32})\/?$/.exec(path);
if (shared) {
  createRoot(root).render(<SharedDocument token={shared[1]!} />);
} else if (path === "/goodbye") {
  const params = new URLSearchParams(window.location.search);
  createRoot(root).render(<Goodbye cancelled={params.get("cancelled") === "1"} signInRemoved={params.get("signin") !== "0"} />);
} else {
  interceptLinks();
  createRoot(root).render(<App />);
}
